// fetchAllPages — the paginated fetch-all core shared by the GA4 paginated hook,
// the Google Ads wasted-spend / search-terms loaders, and the CI rate-calendar
// loader. All three implemented the identical loop by hand: fetch page 1, read
// `pagination.total_pages`, parallel-fetch pages 2..N, concat every page's `items`.
// This is JUST that loop — each caller keeps its own surrounding logic (the GA4
// hook's stale-while-revalidate, Ads' client-side status filter, CI's mock-lodging
// injection, each site's cancellation and error handling). It intentionally does
// NOT own loading/error state; it's a pure async function.
//
// Contract:
//   pageFn(page) → Promise<pageData>, where pageData is the UNWRAPPED response
//   body: an object `{ items, pagination }` (or, when `firstPageBareFallback` is
//   set and the body is itself the array, the array). The caller does its own
//   `resp?.data ?? {}` unwrap inside pageFn so each site keeps its unwrap style.
//
//   Returns Promise<items[]> — the concatenated items across all pages.
//
// @param {(page:number)=>Promise<any>} pageFn
// @param {Object}  [opts]
// @param {boolean} [opts.firstPageBareFallback=false] — if the first page's body
//        has no `.items` (it's already a bare array), use the body itself as the
//        first page's items. Preserves the GA4 hook's `firstData.items || firstData`
//        tolerance; Ads/CI always get `{items,pagination}` so they leave it off.
export async function fetchAllPages(pageFn, { firstPageBareFallback = false } = {}) {
  const firstData = (await pageFn(1)) ?? {};
  const totalPages = firstData?.pagination?.total_pages || 1;

  const firstItems = firstData.items ?? (firstPageBareFallback ? firstData : null) ?? [];
  let allItems = [...firstItems];

  if (totalPages > 1) {
    const remaining = [];
    for (let page = 2; page <= totalPages; page++) {
      remaining.push(pageFn(page));
    }
    const results = await Promise.all(remaining);
    for (const result of results) {
      allItems = allItems.concat(result?.items ?? []);
    }
  }
  return allItems;
}

export default fetchAllPages;
